import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { Link } from "@tanstack/react-router";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { Bell, Settings, ShieldCheck, Star } from "lucide-react";
import { MessageSquare } from "lucide-react";
import { MouseEvent, useEffect, useState } from "react";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { ReactComponent as EditProfileIcon } from "assets/img/icons/header/edit-profile.svg";
import { ReactComponent as HomeIcon } from "assets/img/icons/header/home.svg";
import { ReactComponent as MessagesIcon } from "assets/img/icons/header/inbox.svg";
import { ReactComponent as LogoutIcon } from "assets/img/icons/header/log-out.svg";
import { ReactComponent as NotificationIcon } from "assets/img/icons/header/notifications.svg";
import { Avatar } from "components/elements";
import { AskNavigator } from "components/elements/AskNavigator";
import { formatISODateRelative } from "lib/formatISODate";
import { handleNotificationClick } from "lib/handleNotificationClick";
import { trpc } from "lib/trpc";
import { ClientFriendRequest, ClientNotification } from "@compass/shared/types";
import { ADMIN_URL } from "../../config";

export interface HeaderUser {
    name: string;
    firstName: string;
    lastName: string;
    email?: string;
    avatarUrl?: string | null;
    id?: number | null;
    role?: string;
    receivedFriendRequests?: ClientFriendRequest;
}

type HeaderProps = {
    isLoginPage: boolean;
    isSocialPage: boolean;
    isLoggedIn: boolean;
    user?: HeaderUser;
    notifications: ClientNotification[];
};

export const Header = ({ user, isSocialPage }: HeaderProps) => {
    const [activeTab, setActiveTab] = useState<"all" | "inbox" | "discussions" | "new">("all");
    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const navigate = useNavigate();
    const router = useRouter();
    const pathname = router.state.currentLocation.pathname;

    // fresh data
    const { data: notifications = [], refetch } = trpc.notifications.latest.useQuery(undefined, {
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        staleTime: 30_000,
    });

    // Force a refetch on mount
    useEffect(() => {
        refetch();
    }, [refetch]);

    const filteredNotifications = notifications.filter((n) => {
        if (activeTab === "inbox") return n.data.type === "message";
        if (activeTab === "discussions") return n.data.type === "discussion";
        if (activeTab === "new") return !n.isRead;
        return true;
    });

    const inboxCount = notifications.filter((n) => n.data.type === "message").length;
    const discussionsCount = notifications.filter((n) => n.data.type === "discussion").length;
    const newCount = notifications.filter((n) => !n.isRead).length;

    const showGoBack = pathname.startsWith("/p/");
    const queryClient = trpc.useContext();

    trpc.realtime.onNewNotification.useSubscription(undefined, {
        onData: () => {
            queryClient.notifications.latest.invalidate();
        },
    });

    const unreadNotifications = notifications.filter((n) => !n.isRead).slice(0, 5);
    // const unreadMessages = notifications
    //     .filter((n) => !n.isRead && n.data.type === "message")
    //     .slice(0, 5);
    const markAsReadMutation = trpc.notifications.markAsRead.useMutation();
    const markAllAsReadMutation = trpc.notifications.markAllAsRead.useMutation();

    const handleNotification = (notification: ClientNotification) => {
        handleNotificationClick({
            notification,
            navigate,
            queryClient,
            markAsReadMutation,
        });
    };

    const handleMarkAllAsRead = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        markAllAsReadMutation.mutate(undefined, {
            onSuccess: async () => {
                await queryClient.notifications.latest.invalidate();
                await queryClient.notifications.latest.refetch();
            },
        });
    };

    const hasUnreadNotifications = notifications.some((n) => !n.isRead);

    return (
        <nav>
            <div className="bg-brand-polar mx-auto mt-6 flex h-14 flex-row items-center justify-between rounded-2xl border border-[#c8c8c8] px-[7px] py-[33px]">
                {showGoBack ? (
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center text-sm text-[#424242] hover:text-black"
                    >
                        <ArrowLeftIcon className="mr-2 h-5 w-5" />
                        Go back
                    </button>
                ) : (
                    <AskNavigator />
                )}
                <div className="ml-auto flex items-center gap-1 sm:gap-2 md:gap-3 lg:gap-4">
                    <Link
                        to="/"
                        style={{ textDecoration: "none", color: "inherit" }}
                        className="block text-sm text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-0"
                    >
                        <HomeIcon />
                    </Link>
                    <Link
                        to="/favorites"
                        style={{ textDecoration: "none", color: "inherit" }}
                        className="block text-sm text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-0"
                        aria-label="Favorites"
                    >
                        <Star className="h-6 w-6" />
                    </Link>
                    <button
                        onClick={() =>
                            navigate({ to: isSocialPage ? "/messages" : "/discussions" })
                        }
                        className="relative focus:outline-none focus:ring-0"
                    >
                        <MessagesIcon />
                    </button>

                    <Dropdown.Root
                        onOpenChange={(open) => {
                            if (open) refetch();
                        }}
                    >
                        <Dropdown.Trigger asChild>
                            <button className="relative focus:outline-none focus:ring-0">
                                <NotificationIcon />
                                {hasUnreadNotifications && (
                                    <span className="absolute right-2.5 top-2.5 h-3 w-3 rounded-full bg-red-500"></span>
                                )}
                            </button>
                        </Dropdown.Trigger>
                        <Dropdown.Content className="z-50 mt-3.5 mr-[65px] w-[470px] overflow-hidden rounded-2xl border border-[#d4d5d5] bg-white">
                            {/* Sticky Header */}
                            <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl border-b border-[#d4d5d5] bg-white p-4 pb-0">
                                <div className="flex w-[90%] flex-col">
                                    <div className="flex flex-row items-center justify-between">
                                        <div className="font-sans text-[20px] font-medium leading-normal text-[#424242]">
                                            Notifications
                                        </div>
                                        <a
                                            href="#"
                                            onClick={handleMarkAllAsRead}
                                            className="font-roboto text-[16.9px] font-semibold text-[#37598A] underline"
                                        >
                                            Mark all as read
                                        </a>
                                    </div>
                                    <div className="mt-3 flex">
                                        {[
                                            {
                                                key: "all",
                                                label: "All",
                                                count: notifications.length,
                                            },
                                            { key: "inbox", label: "Inbox", count: inboxCount },
                                            {
                                                key: "discussions",
                                                label: "Discussions",
                                                count: discussionsCount,
                                            },
                                            { key: "new", label: "New", count: newCount },
                                        ].map((tab) => (
                                            <button
                                                key={tab.key}
                                                onClick={() =>
                                                    setActiveTab(
                                                        tab.key as
                                                            | "all"
                                                            | "inbox"
                                                            | "discussions"
                                                            | "new",
                                                    )
                                                }
                                                className={`mr-6 flex items-center pb-2 text-sm font-medium ${
                                                    activeTab === tab.key
                                                        ? "border-b-2 border-[#08abf2] font-semibold text-[#37598A]"
                                                        : "text-gray-600 hover:text-gray-800"
                                                }`}
                                            >
                                                {tab.label}
                                                {tab.count > 0 && (
                                                    <span
                                                        className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold text-white ${
                                                            activeTab === tab.key
                                                                ? "bg-[#08abf2]"
                                                                : "bg-[#08abf2] opacity-80"
                                                        }`}
                                                    >
                                                        {tab.count}
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <Dropdown.Item asChild>
                                    <button
                                        className="-mt-10 text-gray-500 hover:text-gray-700"
                                        aria-label="Close"
                                    >
                                        <CloseIcon className="h-[30px] w-[30px]" />
                                    </button>
                                </Dropdown.Item>
                            </div>

                            {/* Scrollable List */}
                            <div className="max-h-[440px] overflow-y-auto rounded-b-2xl">
                                {filteredNotifications.length > 0 ? (
                                    <ul className="space-y-0 pl-0">
                                        {filteredNotifications.map((n) => (
                                            <li
                                                key={n.id}
                                                className={`${n.isRead ? "bg-[#ffffff]" : "bg-[#f5f5f5]"} relative cursor-pointer rounded-none border-b border-[#d4d5d5] p-4 last:border-b-0 hover:bg-[#f5f5f5]`}
                                                onClick={() => handleNotification(n)}
                                            >
                                                <Dropdown.Item asChild>
                                                    <button className="text-left !outline-none">
                                                        <p className="font-sans text-[14px] font-normal leading-normal text-[#424242]">
                                                            {n.data.message}
                                                        </p>
                                                        <p className="font-sans text-[12px] font-light leading-normal text-[#424242]">
                                                            {formatISODateRelative(n.createdAt)}
                                                        </p>
                                                        {!n.isRead && (
                                                            <div className="bg-brand-atlantic absolute right-5 top-4 h-3 w-3 rounded-full"></div>
                                                        )}
                                                    </button>
                                                </Dropdown.Item>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="p-4 font-sans text-[14px] font-normal leading-normal text-[#424242]">
                                        No new notifications.
                                    </p>
                                )}
                            </div>
                        </Dropdown.Content>
                    </Dropdown.Root>

                    {showGoBack && (
                        <AskNavigator
                            trigger={
                                <button className="hidden items-center gap-3 rounded-full border border-[#E6F6FB] bg-white px-3 py-1.5 text-sm font-semibold text-[#0F8EA6] shadow-sm hover:bg-slate-50 md:inline-flex">
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#E6F6FB]">
                                        <MessageSquare className="h-4 w-4 text-[#0F8EA6]" />
                                    </span>
                                    <span className="-mt-px">Ask</span>
                                </button>
                            }
                        />
                    )}

                    {/* Profile Dropdown */}
                    <Dropdown.Root open={profileDropdownOpen} onOpenChange={setProfileDropdownOpen}>
                        <Dropdown.Trigger asChild>
                            <button className="relative focus:outline-none focus:ring-0">
                                {user?.avatarUrl ? (
                                    <Avatar
                                        photoUrl={user?.avatarUrl}
                                        name={user?.name}
                                        className="h-[50px] w-[50px] rounded-xl text-black shadow-xl"
                                    />
                                ) : (
                                    <div className="bg-brand-polarice flex h-[50px] w-[50px] items-center justify-center rounded-2xl p-1.5 font-semibold uppercase text-[#19243d]">
                                        {user?.firstName?.[0] || ""}
                                        {user?.lastName?.[0] || ""}
                                    </div>
                                )}
                            </button>
                        </Dropdown.Trigger>
                        <Dropdown.Content className="z-50 mt-3.5 mr-[65px] w-[290px] rounded-2xl border border-[#d4d5d5] bg-white">
                            <div className="flex items-center justify-between rounded-2xl border-b border-[#d4d5d5] p-4">
                                <div className="truncate font-sans text-[20px] font-medium leading-normal text-[#424242]">
                                    {user?.name}
                                </div>
                                <Dropdown.Item asChild>
                                    <button className="ml-auto" aria-label="Close">
                                        <CloseIcon className="h-[30px] w-[30px]" />
                                    </button>
                                </Dropdown.Item>
                            </div>
                            {/* Menu Options */}
                            <div className="space-y-2 p-4">
                                <ul className="mt-2 list-none space-y-3 p-0">
                                    <li className="rounded-full bg-[#eeeeee] p-2 px-4">
                                        <Dropdown.Item asChild>
                                            <Link
                                                to="/profile/$id"
                                                style={{ textDecoration: "none", color: "inherit" }}
                                                params={{ id: user?.id?.toString() ?? "" }}
                                                className="block font-sans text-[17px] font-normal leading-normal text-[#424242] focus:outline-none focus:ring-0"
                                            >
                                                <div className="flex flex-row space-x-2">
                                                    <div>
                                                        <EditProfileIcon className="h-[24px] w-[24px]" />
                                                    </div>
                                                    <div>Account settings</div>
                                                </div>
                                            </Link>
                                        </Dropdown.Item>
                                    </li>
                                    <li className="rounded-full bg-[#eeeeee] p-2 px-4">
                                        <Dropdown.Item asChild>
                                            <Link
                                                to="/settings/notifications"
                                                style={{ textDecoration: "none", color: "inherit" }}
                                                params={{}}
                                                className="block font-sans text-[17px] font-normal leading-normal text-[#424242] focus:outline-none focus:ring-0"
                                            >
                                                <div className="flex flex-row space-x-2">
                                                    <div>
                                                        <Bell className="h-[24px] w-[24px]" />
                                                    </div>
                                                    <div>Notification Settings</div>
                                                </div>
                                            </Link>
                                        </Dropdown.Item>
                                    </li>
                                    <li className="rounded-full bg-[#eeeeee] p-2 px-4">
                                        <Dropdown.Item asChild>
                                            <Link
                                                to="/settings/security"
                                                style={{ textDecoration: "none", color: "inherit" }}
                                                params={{}}
                                                className="block font-sans text-[17px] font-normal leading-normal text-[#424242] focus:outline-none focus:ring-0"
                                            >
                                                <div className="flex flex-row space-x-2">
                                                    <div>
                                                        <Settings className="h-[24px] w-[24px]" />
                                                    </div>
                                                    <div>Security</div>
                                                </div>
                                            </Link>
                                        </Dropdown.Item>
                                    </li>
                                    {user?.role === "COMPASS_SUPERUSER" && (
                                        <li className="rounded-full bg-[#eeeeee] p-2 px-4">
                                            <Dropdown.Item asChild>
                                                <a
                                                    href={ADMIN_URL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block font-sans text-[17px] font-normal leading-normal text-[#424242] no-underline focus:outline-none focus:ring-0"
                                                >
                                                    <div className="flex flex-row space-x-2">
                                                        <div>
                                                            <ShieldCheck className="h-[24px] w-[24px]" />
                                                        </div>
                                                        <div>Admin Panel</div>
                                                    </div>
                                                </a>
                                            </Dropdown.Item>
                                        </li>
                                    )}
                                    <li className="rounded-full bg-[#eeeeee] p-2 px-4">
                                        <Dropdown.Item asChild>
                                            <Link
                                                to="/logout"
                                                style={{ textDecoration: "none", color: "inherit" }}
                                                className="block font-sans text-[17px] font-normal leading-normal text-[#424242] focus:outline-none focus:ring-0"
                                            >
                                                <div className="flex flex-row space-x-2">
                                                    <div>
                                                        <LogoutIcon className="h-[24px] w-[24px]" />
                                                    </div>
                                                    <div>Log Out</div>
                                                </div>
                                            </Link>
                                        </Dropdown.Item>
                                    </li>
                                </ul>
                            </div>
                        </Dropdown.Content>
                    </Dropdown.Root>
                </div>
            </div>
        </nav>
    );
};
